import { AppConstants } from '@/helpers/app.constants';
import { EnumHelper } from '@/helpers/enumHelper';
import { PropertyHelper } from '@/helpers/property.helper';
import { Utils } from '@/helpers/utils';
import { AlertService } from '@/services/alert.service';
import { Component, Inject, LOCALE_ID, OnDestroy, OnInit } from '@angular/core';
import {
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import {
  Document,
  DocumentSummary,
  EnergyClassification,
  EnergyType,
  PropertiesService,
  PropertyEquipment,
  PropertyEquipmentStatus,
  PropertyEquipmentType,
  RealProperty,
  VaultService
} from '@eemo/api-client';
import { Subscription } from 'rxjs';
import { DocAddWidgetComponent } from '../doc-add-widget/doc-add-widget.component';
import { PropertyWorkDialogComponent } from '../property-work-dialog/property-work-dialog.component';
import { SelectComponent } from '../ui-forms/select/select.component';

import { ObjectUpdatedType } from '@/enums/ObjectUpdatedType.enum';
import { AppConfigService } from '@/services/app-config.service';
import { NgFor, NgTemplateOutlet } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { ModalBaseComponent } from '../base/modal-base/modal-base.component';
import { InputComponent } from '../ui-forms/input/input.component';

export class PropertyEquipmentDialogData {
  prop: RealProperty;
  equipment?: PropertyEquipment;
  edition = false;
}

@Component({
  selector: 'app-property-equipment-dialog',
  templateUrl: './property-equipment-dialog.component.html',
  styleUrls: ['./property-equipment-dialog.component.scss'],
  standalone: true,
  imports: [
    ModalBaseComponent,
    NgTemplateOutlet,
    MatDialogModule,
    FormsModule,
    ReactiveFormsModule,
    SelectComponent,
    DocAddWidgetComponent,
    InputComponent,
    NgFor,
    TranslateModule
  ]
})
export class PropertyEquipmentDialogComponent implements OnInit, OnDestroy {
  isBrowserSupportsDateInput = Utils.isBrowserSupportsDateInput;

  subs: Subscription[] = [];

  form: UntypedFormGroup;
  dialogTitle: string;
  formSubmitting = false;

  typeList: PropertyEquipmentType[] = EnumHelper.getApiEnumList(PropertyEquipmentType);
  statusList: PropertyEquipmentStatus[] = EnumHelper.getApiEnumList(PropertyEquipmentStatus);
  energyTypeList: EnergyType[] = EnumHelper.getApiEnumList(EnergyType);
  energyClassificationList: EnergyClassification[] = EnumHelper.getApiEnumList(EnergyClassification);

  documents: Document[] = [];
  newDocuments: DocumentSummary[] = [];
  // array used to simulate a classic for loop (for(int i=0;i<5;i++)
  newDocumentsCount = [1];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: PropertyEquipmentDialogData,
    private propertyService: PropertiesService,
    private alertService: AlertService,
    private fb: UntypedFormBuilder,
    public propertyHelper: PropertyHelper,
    public dialogRef: MatDialogRef<PropertyWorkDialogComponent>,
    private vaultService: VaultService,
    private appConfigService: AppConfigService,
    @Inject(LOCALE_ID) public locale: string
  ) {
    this.dialogTitle = this.data.edition ? "Modifier l'équipement" : 'Ajouter un équipement';
  }

  get form_name() {
    return this.form.get('name') as FormControl<string>;
  }
  get form_description() {
    return this.form.get('description') as FormControl<string>;
  }
  get form_brand() {
    return this.form.get('brand') as FormControl<string>;
  }
  get form_model() {
    return this.form.get('model') as FormControl<string>;
  }
  get form_serialNumber() {
    return this.form.get('serialNumber') as FormControl<string>;
  }
  get form_type() {
    return this.form.get('type') as FormControl<PropertyEquipmentType>;
  }
  get form_energyType() {
    return this.form.get('energyType') as FormControl<EnergyType>;
  }
  get form_power() {
    return this.form.get('power') as FormControl<number>;
  }
  get form_energyClassification() {
    return this.form.get('energyClassification') as FormControl<EnergyClassification>;
  }
  get form_status() {
    return this.form.get('status') as FormControl<PropertyEquipmentStatus>;
  }
  get form_dateInstallation() {
    return this.form.get('dateInstallation') as FormControl<Date>;
  }
  get form_dateRemoval() {
    return this.form.get('dateRemoval') as FormControl<Date>;
  }
  get form_datePurchase() {
    return this.form.get('datePurchase') as FormControl<Date>;
  }
  get form_cost() {
    return this.form.get('cost') as FormControl<number>;
  }
  get form_warrantyPeriod() {
    return this.form.get('warrantyPeriod') as FormControl<number>;
  }

  ngOnInit() {
    this.form = this.fb.group({
      name: [null, [Validators.required, Validators.maxLength(50)]],
      description: [null, Validators.maxLength(160)],
      brand: [null, Validators.maxLength(50)],
      model: [null, Validators.maxLength(50)],
      serialNumber: [null, Validators.maxLength(50)],
      type: [null, Validators.required],
      energyType: [null, Validators.required],
      power: [null, [Validators.pattern(AppConstants.FORM_PATTERN_POSITIVE_INTEGER), Validators.min(1)]],
      energyClassification: [null],
      status: [null, Validators.required],
      dateInstallation: [null, [Validators.required]],
      dateRemoval: [null],
      datePurchase: [null],
      cost: [null, [Validators.pattern(AppConstants.FORM_PATTERN_POSITIVE_DECIMAL), Validators.min(0)]],
      warrantyPeriod: [null, [Validators.pattern(AppConstants.FORM_PATTERN_POSITIVE_INTEGER), Validators.min(0)]]
    });

    if (this.data.equipment) {
      this.form.patchValue(this.data.equipment);
      this.data.equipment.documents.forEach((d) => {
        this.subs.push(this.vaultService.getDocument(d.uid).subscribe((doc) => this.documents.push(doc)));
      });
    }
  }

  ngOnDestroy() {
    this.subs.forEach((s) => s.unsubscribe());
  }

  onNewDocSelected(doc: DocumentSummary) {
    if (doc) {
      this.newDocuments.push(doc);
      this.newDocumentsCount.push(1);
    }
  }

  onExistingDocDeleted(doc: DocumentSummary) {
    if (doc) {
      // doc deleted
      this.documents = this.documents.filter((d) => d.uid != doc.uid);
    }
  }

  onNewDocDeleted(doc: DocumentSummary) {
    if (doc) {
      // new doc deleted
      this.newDocuments = this.newDocuments.filter((d) => d.uid != doc.uid);
      this.newDocumentsCount.pop();
    }
  }

  onSubmit() {
    if (this.form.valid) {
      this.formSubmitting = true;
      const equipment: PropertyEquipment = this.form.value;

      equipment.documents = [];
      this.documents.forEach((d) => equipment.documents.push(this.propertyHelper.mapToDocumentSummary(d)));
      this.newDocuments.forEach((d) => equipment.documents.push(d));

      if (this.data.edition) {
        this.subs.push(
          this.propertyService
            .updateRealPropertyEquipment(this.data.prop.uid, this.data.equipment.uid, this.form.value)
            .subscribe({
              next: () => {
                this.dialogRef.close(true);
                this.alertService.success('Mise à jour effectuée');
                this.appConfigService.triggerObjectUpdated(ObjectUpdatedType.EQUIPMENT);
              },
              error: () => {
                this.dialogRef.close();
              }
            })
        );
      } else {
        this.subs.push(
          this.propertyService.createRealPropertyEquipment(this.data.prop.uid, this.form.value).subscribe({
            next: (createdId) => {
              this.dialogRef.close(createdId);
              this.alertService.success('Création effectuée');
              this.appConfigService.triggerObjectUpdated(ObjectUpdatedType.EQUIPMENT);
            },
            error: () => {
              this.dialogRef.close();
            }
          })
        );
      }
    }
  }
  onNoClick(): void {
    this.dialogRef.close(false);
  }
}
